// SessionIdleWarning — the "you're about to be signed out" notice.
//
// Grant, 2026-09-04: "if someone is literally in the middle of working it
// will auto log them out with no warning. this cant happen."
//
// Working no longer runs the clock out at all — the server-side fix in
// workers/api/src/lib/session-renewal.ts restored the sliding idle window
// this app always claimed to have. What is left is the honest case: you
// really did step away for hours, the session really should lapse, and you
// should be told before it does rather than discovering it when a form you
// just filled in bounces to a sign-in screen.
//
// It never phones home on its own — see session-idle.ts for why that rule
// matters. Every renewal traces back to a person: normal work, or the
// button in this banner.

import { useCallback, useEffect, useState } from "react";

import { useSession } from "../session";
import {
  formatCountdown,
  idleState,
  lastActivityAt,
  type IdlePhase,
} from "../session-idle";

// While the banner is up, tick once a second so the countdown reads like a
// countdown. The rest of the time this is just a cheap "are we close yet?"
// check against a number already in memory, so it can be lazy.
const TICK_MS_WHILE_SHOWING = 1_000;
const TICK_MS_WHILE_IDLE = 15_000;

export function SessionIdleWarning() {
  const { status, refresh } = useSession();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);

  const signedIn = status === "signed_in";
  const state = signedIn ? idleState(lastActivityAt(), now) : null;
  const phase: IdlePhase = state?.phase ?? "active";
  const showing = phase !== "active";

  useEffect(() => {
    if (!signedIn) return undefined;
    const tick = () => setNow(Date.now());
    const interval = window.setInterval(
      tick,
      showing ? TICK_MS_WHILE_SHOWING : TICK_MS_WHILE_IDLE,
    );
    // Coming back to a backgrounded tab is exactly when this matters and
    // exactly when timers have been throttled, so re-check immediately
    // rather than waiting out an interval the browser stretched.
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [signedIn, showing]);

  const handleStaySignedIn = useCallback(async () => {
    setBusy(true);
    try {
      // A real request, so the server slides the window exactly as it does
      // for any other work. If the session is already gone this comes back
      // with no user and the app moves to the sign-in screen on its own.
      await refresh();
      setNow(Date.now());
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  if (!showing || !state) return null;

  const lapsed = phase === "expired";

  return (
    <div
      className="session-idle-warning"
      role="status"
      aria-live="polite"
      data-testid="session-idle-warning"
      data-phase={phase}
    >
      <p className="session-idle-warning-title">
        {lapsed ? "You may have been signed out" : "You'll be signed out soon"}
      </p>
      <p className="session-idle-warning-body">
        {lapsed
          ? "It's been quiet long enough that your session may have ended. Pick up where you left off:"
          : `There's been no activity for a while, so you'll be signed out in about ${formatCountdown(
              state.msRemaining,
            )}.`}
      </p>
      <button
        type="button"
        className="session-idle-warning-action"
        onClick={handleStaySignedIn}
        disabled={busy}
        data-testid="session-idle-stay"
      >
        {busy ? "One moment…" : "Stay signed in"}
      </button>
    </div>
  );
}
